/**
 * Google Drive Picker integration.
 * Lets a user pick a PDF/DOCX from their Google Drive and returns it as a native File
 * that can be fed directly into the existing resume upload pipeline.
 */

declare global {
  interface Window {
    gapi: any;
    google: any;
  }
}

const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
const API_KEY = import.meta.env.VITE_GOOGLE_API_KEY as string | undefined;
const APP_ID = import.meta.env.VITE_GOOGLE_APP_ID as string | undefined;

const SCOPES = "https://www.googleapis.com/auth/drive.readonly";
const PDF_MIME = "application/pdf";
const DOCX_MIME =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

let pickerApiLoaded = false;
let gapiLoadingPromise: Promise<void> | null = null;
let gisReadyPromise: Promise<void> | null = null;

function waitForGlobal(check: () => boolean, timeoutMs = 10000): Promise<void> {
  return new Promise((resolve, reject) => {
    const start = Date.now();
    const tick = () => {
      if (check()) return resolve();
      if (Date.now() - start > timeoutMs) {
        return reject(new Error("Timed out waiting for Google scripts to load"));
      }
      setTimeout(tick, 50);
    };
    tick();
  });
}

function loadPickerApi(): Promise<void> {
  if (pickerApiLoaded) return Promise.resolve();
  if (gapiLoadingPromise) return gapiLoadingPromise;

  gapiLoadingPromise = waitForGlobal(() => !!window.gapi).then(
    () =>
      new Promise<void>((resolve, reject) => {
        window.gapi.load("picker", {
          callback: () => {
            pickerApiLoaded = true;
            resolve();
          },
          onerror: () => reject(new Error("Failed to load Google Picker API")),
        });
      })
  );

  return gapiLoadingPromise;
}

function ensureGis(): Promise<void> {
  if (gisReadyPromise) return gisReadyPromise;
  gisReadyPromise = waitForGlobal(
    () => !!window.google?.accounts?.oauth2
  );
  return gisReadyPromise;
}

async function requestAccessToken(): Promise<string> {
  if (!CLIENT_ID) {
    throw new Error(
      "Google Client ID is not configured. Set VITE_GOOGLE_CLIENT_ID in your environment."
    );
  }

  await ensureGis();

  return new Promise<string>((resolve, reject) => {
    try {
      const tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: CLIENT_ID,
        scope: SCOPES,
        callback: (response: { access_token?: string; error?: string }) => {
          if (response.error || !response.access_token) {
            reject(
              new Error(response.error || "Failed to obtain Google access token")
            );
            return;
          }
          resolve(response.access_token);
        },
      });
      tokenClient.requestAccessToken({ prompt: "" });
    } catch (err) {
      reject(err instanceof Error ? err : new Error(String(err)));
    }
  });
}

interface PickedFile {
  id: string;
  name: string;
  mimeType: string;
}

function showPicker(accessToken: string): Promise<PickedFile> {
  if (!API_KEY) {
    return Promise.reject(
      new Error(
        "Google API Key is not configured. Set VITE_GOOGLE_API_KEY in your environment."
      )
    );
  }
  if (!APP_ID) {
    return Promise.reject(
      new Error(
        "Google App ID (project number) is not configured. Set VITE_GOOGLE_APP_ID in your environment."
      )
    );
  }

  return new Promise((resolve, reject) => {
    const view = new window.google.picker.DocsView(
      window.google.picker.ViewId.DOCS
    )
      .setMimeTypes(`${PDF_MIME},${DOCX_MIME}`)
      .setIncludeFolders(true)
      .setSelectFolderEnabled(false);

    const picker = new window.google.picker.PickerBuilder()
      .enableFeature(window.google.picker.Feature.NAV_HIDDEN)
      .setAppId(APP_ID)
      .setOAuthToken(accessToken)
      .addView(view)
      .setDeveloperKey(API_KEY)
      .setCallback((data: any) => {
        const action = data[window.google.picker.Response.ACTION];
        if (action === window.google.picker.Action.PICKED) {
          const doc = data[window.google.picker.Response.DOCUMENTS]?.[0];
          if (!doc) {
            reject(new Error("No file was selected"));
            return;
          }
          resolve({
            id: doc[window.google.picker.Document.ID],
            name: doc[window.google.picker.Document.NAME],
            mimeType: doc[window.google.picker.Document.MIME_TYPE],
          });
        } else if (action === window.google.picker.Action.CANCEL) {
          reject(new Error("Picker cancelled"));
        }
      })
      .build();

    picker.setVisible(true);
  });
}

async function downloadFile(
  fileId: string,
  accessToken: string,
  name: string,
  mimeType: string
): Promise<File> {
  const res = await fetch(
    `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(
      fileId
    )}?alt=media`,
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    }
  );
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `Failed to download file from Google Drive (${res.status}): ${text}`
    );
  }
  const blob = await res.blob();
  return new File([blob], name, { type: mimeType || blob.type });
}

/**
 * Open Google Drive picker and resolve with the selected file as a native File object.
 * Throws if cancelled, misconfigured, or download fails.
 */
export async function pickResumeFromDrive(): Promise<File> {
  if (!CLIENT_ID || !API_KEY || !APP_ID) {
    throw new Error(
      "Google Drive Picker is not configured. Missing VITE_GOOGLE_CLIENT_ID, VITE_GOOGLE_API_KEY, or VITE_GOOGLE_APP_ID."
    );
  }

  await loadPickerApi();
  const token = await requestAccessToken();
  const picked = await showPicker(token);
  return downloadFile(picked.id, token, picked.name, picked.mimeType);
}

export function isGoogleDriveConfigured(): boolean {
  return Boolean(CLIENT_ID && API_KEY && APP_ID);
}
